import React, { useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { designSystem, reactHookForm } from '@yola/ws-ui';
import { utils } from '@yola/ws-sdk';
import constants from '../constants';
import { createKeywordOption } from '../helpers/get-keywords-list';
import validateKeywords from '../helpers/validate-keywords';
import validateCanonicalUrl from '../helpers/validate-canonical-url';
import SearchPreview from './search-preview';

const {
  Stack,
  Heading,
  ControlGroup,
  CheckboxGroup,
  Mark,
  InputGroupField,
  CheckboxField,
  TextAreaField,
  TagInputField,
  FieldStatus,
} = designSystem;
const { useWatch, useFormState } = reactHookForm;

const { fieldNames } = constants;

function SeoSection({
  control,
  triggerValidation,
  fieldRefs,
  captions,
  pathPrefix,
  businessName,
  isBusinessInfoInWebsiteSettingsFeatureFlagEnabled,
  onChangeSameAsPageName,
  onChangeSeoTitle,
  onChangeSeoDescription,
  onChangeSameAsPageUrl,
}) {
  const hasUserChangedKeywordsRef = useRef(false);

  const handleKeywordsChange = useCallback(() => {
    hasUserChangedKeywordsRef.current = true;
    triggerValidation(fieldNames.KEYWORDS);
  }, [triggerValidation]);

  const [
    pageTitleValue,
    pagePathValue,
    pageDescriptionValue,
    sameAsPageName,
    displayBusinessName,
    sameAsPageUrl,
  ] = useWatch({
    control,
    name: [
      fieldNames.TITLE,
      fieldNames.PATH,
      fieldNames.DESCRIPTION,
      fieldNames.SAME_AS_PAGE_NAME,
      fieldNames.DISPLAY_BUSINESS_NAME,
      fieldNames.USE_PAGE_URL_FOR_CANONICAL_URL,
    ],
  });

  const { errors } = useFormState({
    control,
    name: [fieldNames.KEYWORDS, fieldNames.CANONICAL_URL],
  });
  const hasKeywordsError = Boolean(errors[fieldNames.KEYWORDS]);
  const hasCanonicalUrlError = Boolean(errors[fieldNames.CANONICAL_URL]);

  let title = pageTitleValue;
  const path = `${pathPrefix}${pagePathValue}`;
  const description = pageDescriptionValue?.trim()
    ? pageDescriptionValue
    : captions.searchPreview.description;

  if (isBusinessInfoInWebsiteSettingsFeatureFlagEnabled && businessName && displayBusinessName) {
    const unescapedBusinessName = utils.unescapeHTML(businessName);

    if (title) {
      title = `${title} | ${unescapedBusinessName}`;
    } else {
      title = unescapedBusinessName;
    }
  }

  return (
    <Stack gap="spacing-m">
      <Stack gap="spacing-3-xs">
        <Heading type="heading-6">{captions.searchPreview.heading}</Heading>
        <SearchPreview
          title={title || captions.searchPreview.title}
          path={path}
          description={description}
        />
      </Stack>

      <ControlGroup title={captions.title.name}>
        <div ref={fieldRefs[fieldNames.TITLE]} className="ws-page-settings__field-wrapper">
          <InputGroupField
            name={fieldNames.TITLE}
            control={control}
            disabled={sameAsPageName}
            onChange={onChangeSeoTitle}
          />
          <FieldStatus text={captions.title.description} />
        </div>
      </ControlGroup>

      <CheckboxGroup>
        <CheckboxField
          name={fieldNames.SAME_AS_PAGE_NAME}
          control={control}
          label={captions.sameAsPageName.label}
          onChange={onChangeSameAsPageName}
        />

        {isBusinessInfoInWebsiteSettingsFeatureFlagEnabled && (
          <CheckboxField
            name={fieldNames.DISPLAY_BUSINESS_NAME}
            control={control}
            label={captions.displayBusinessName.label}
            description={
              !businessName && (
                <>
                  {captions.displayBusinessName.description}
                  <Mark appearance="high-emphasis">
                    {captions.displayBusinessName.settingsTitle} &#x2192;{' '}
                    {captions.displayBusinessName.businessInfoTitle}
                  </Mark>
                </>
              )
            }
            disabled={!businessName}
          />
        )}
      </CheckboxGroup>

      <CheckboxField
        name={fieldNames.PREVENT_INDEXING}
        control={control}
        label={captions.preventIndexing.name}
        description={captions.preventIndexing.description}
      />

      <ControlGroup title={captions.description.name}>
        <div ref={fieldRefs[fieldNames.DESCRIPTION]} className="ws-page-settings__field-wrapper">
          <TextAreaField
            name={fieldNames.DESCRIPTION}
            control={control}
            resize
            onChange={onChangeSeoDescription}
          />
          <FieldStatus text={captions.description.description} />
        </div>
      </ControlGroup>

      <ControlGroup title={captions.keywords.name}>
        <div ref={fieldRefs[fieldNames.KEYWORDS]} className="ws-page-settings__field-wrapper">
          <TagInputField
            name={fieldNames.KEYWORDS}
            control={control}
            rules={{
              validate: (value) =>
                hasUserChangedKeywordsRef.current ? validateKeywords(value) : null,
            }}
            isTagTextClipped={false}
            shouldCreateTagOnPaste
            onCreateOption={createKeywordOption}
            onChange={handleKeywordsChange}
          />
          {!hasKeywordsError && <FieldStatus text={captions.keywords.description} />}
        </div>
      </ControlGroup>

      <Stack gap="spacing-3-xs">
        <ControlGroup title={captions.canonicalUrl.name}>
          <div
            ref={fieldRefs[fieldNames.CANONICAL_URL]}
            className="ws-page-settings__field-wrapper"
          >
            <InputGroupField
              name={fieldNames.CANONICAL_URL}
              control={control}
              iconGlyph="link"
              placeholder="https://"
              disabled={sameAsPageUrl}
              rules={{
                validate: (value) => {
                  if (sameAsPageUrl || !value?.trim()) {
                    return null;
                  }
                  return validateCanonicalUrl(value);
                },
              }}
            />
            {!hasCanonicalUrlError && <FieldStatus text={captions.canonicalUrl.description} />}
          </div>
        </ControlGroup>

        <CheckboxField
          name={fieldNames.USE_PAGE_URL_FOR_CANONICAL_URL}
          control={control}
          label={captions.sameAsPageUrl.label}
          onChange={onChangeSameAsPageUrl}
        />
      </Stack>
    </Stack>
  );
}

SeoSection.propTypes = {
  control: PropTypes.shape().isRequired,
  triggerValidation: PropTypes.func.isRequired,
  fieldRefs: PropTypes.shape().isRequired,
  captions: PropTypes.shape().isRequired,
  pathPrefix: PropTypes.string.isRequired,
  businessName: PropTypes.string.isRequired,
  isBusinessInfoInWebsiteSettingsFeatureFlagEnabled: PropTypes.bool.isRequired,
  onChangeSameAsPageName: PropTypes.func.isRequired,
  onChangeSeoTitle: PropTypes.func.isRequired,
  onChangeSeoDescription: PropTypes.func.isRequired,
  onChangeSameAsPageUrl: PropTypes.func.isRequired,
};

export default SeoSection;
